import type { Editor, JSONContent } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { useCallback, useEffect, useRef } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

import { hasPendingSlashTrigger, slashCommandsPluginKey } from './slashCore';

type OutputFormat = 'json' | 'html';
type HiddenInputElement = HTMLInputElement | HTMLTextAreaElement;

type ToEditorContent = (serialized: string) => { content: JSONContent | string; format: OutputFormat };
type ToSerializedValue = (editor: Editor, format: OutputFormat) => string;

type UseHiddenInputSyncParams = {
  hiddenInputId?: string | undefined;
  externalValueSourceId?: string | undefined;
  editor: Editor | null;
  enabled?: boolean;
  isControlled: boolean;
  resolvedOutputFormat: OutputFormat;
  toEditorContent: ToEditorContent;
  toSerializedValue: ToSerializedValue;
  isApplyingExternalValueRef: MutableRefObject<boolean>;
  lastValueRef: MutableRefObject<string | null>;
  serializedValueRef: MutableRefObject<string>;
  setUncontrolledValue: Dispatch<SetStateAction<string>>;
  onExternalValueApplied?: (nextValue: string, doc: ProseMirrorNode) => void;
};

const isHiddenInputElement = (element: HTMLElement | null): element is HiddenInputElement =>
  element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement;

export const useHiddenInputSync = ({
  hiddenInputId,
  externalValueSourceId,
  editor,
  enabled = true,
  isControlled,
  resolvedOutputFormat,
  toEditorContent,
  toSerializedValue,
  isApplyingExternalValueRef,
  lastValueRef,
  serializedValueRef,
  setUncontrolledValue,
  onExternalValueApplied,
}: UseHiddenInputSyncParams) => {
  const toEditorContentRef = useRef(toEditorContent);
  const toSerializedValueRef = useRef(toSerializedValue);
  const lastDispatchedInputValueRef = useRef<string | null>(null);
  const previousDispatchedInputValueRef = useRef<string | null>(null);
  const lastDispatchedInputAtRef = useRef<number>(0);
  const attachedInputRef = useRef<HiddenInputElement | null>(null);
  const attachedSourceRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    toEditorContentRef.current = toEditorContent;
  }, [toEditorContent]);

  useEffect(() => {
    toSerializedValueRef.current = toSerializedValue;
  }, [toSerializedValue]);

  const getAttachedInput = useCallback(() => {
    if (!hiddenInputId) return null;
    const element = attachedInputRef.current;
    if (!element?.isConnected) return null;
    return document.getElementById(hiddenInputId) === element ? element : null;
  }, [hiddenInputId]);

  const getAttachedSource = useCallback(() => {
    if (!externalValueSourceId) return null;
    const element = attachedSourceRef.current;
    if (!element?.isConnected) return null;
    return document.getElementById(externalValueSourceId) === element ? element : null;
  }, [externalValueSourceId]);

  const updateExternalSourceValue = useCallback(
    (nextValue: string) => {
      const element = getAttachedSource();
      if (!element) return;
      if (element.getAttribute('data-supertext-value') === nextValue) return;
      element.setAttribute('data-supertext-value', nextValue);
    },
    [getAttachedSource],
  );

  const updateHiddenInputValue = useCallback(
    (nextValue: string, dispatch = true) => {
      if (!hiddenInputId) return;
      const element = getAttachedInput();
      if (!element) return;
      if (element.value === nextValue) return;
      element.value = nextValue;
      if (!dispatch) return;
      previousDispatchedInputValueRef.current = lastDispatchedInputValueRef.current;
      lastDispatchedInputValueRef.current = nextValue;
      lastDispatchedInputAtRef.current = Date.now();
      element.dispatchEvent(new Event('input', { bubbles: true }));
      updateExternalSourceValue(nextValue);
    },
    [hiddenInputId, getAttachedInput, updateExternalSourceValue],
  );

  const getHiddenInputValue = useCallback(() => {
    if (!hiddenInputId) return undefined;
    return getAttachedInput()?.value;
  }, [hiddenInputId, getAttachedInput]);

  const triggerHiddenInputChange = useCallback(
    (forcedValue?: string) => {
      if (!hiddenInputId) return;
      const element = getAttachedInput();
      if (!element) return;
      if (typeof forcedValue === 'string' && element.value !== forcedValue) {
        element.value = forcedValue;
      }
      const currentValue = element.value;
      const currentKnownValue = lastValueRef.current ?? serializedValueRef.current;
      const inputValueChanged =
        lastDispatchedInputValueRef.current !== currentValue && currentKnownValue !== currentValue;
      if (inputValueChanged) {
        previousDispatchedInputValueRef.current = lastDispatchedInputValueRef.current;
        lastDispatchedInputAtRef.current = Date.now();
        element.dispatchEvent(new Event('input', { bubbles: true }));
        lastDispatchedInputValueRef.current = currentValue;
      } else {
        lastDispatchedInputValueRef.current = currentValue;
      }
      updateExternalSourceValue(currentValue);
      element.dispatchEvent(new Event('blur'));
    },
    [hiddenInputId, getAttachedInput, updateExternalSourceValue],
  );

  useEffect(() => {
    if (!enabled) return;
    if (!editor) return;
    if (!hiddenInputId && !externalValueSourceId) return;

    let attachedInput: HiddenInputElement | null = null;
    let inputObserver: MutationObserver | null = null;
    let attachedSource: HTMLElement | null = null;
    let sourceObserver: MutationObserver | null = null;
    let hasAttachedInput = false;
    let hasAttachedSource = false;

    const isCurrentInput = () =>
      Boolean(attachedInput?.isConnected && hiddenInputId && document.getElementById(hiddenInputId) === attachedInput);

    const isCurrentSource = () =>
      Boolean(
        attachedSource?.isConnected &&
          externalValueSourceId &&
          document.getElementById(externalValueSourceId) === attachedSource,
      );

    const handleExternalValue = (nextValue: string) => {
      const currentKnown = lastValueRef.current ?? serializedValueRef.current;
      if (nextValue === currentKnown) return;

      const isImmediateRollbackToPreviousLocalValue =
        nextValue === previousDispatchedInputValueRef.current &&
        currentKnown === lastDispatchedInputValueRef.current &&
        Date.now() - lastDispatchedInputAtRef.current < 1000;

      if (isImmediateRollbackToPreviousLocalValue) {
        updateHiddenInputValue(currentKnown, false);
        updateExternalSourceValue(currentKnown);
        return;
      }

      if (editor.isFocused) {
        const currentEditorValue = toSerializedValueRef.current(editor, resolvedOutputFormat);
        if (nextValue !== currentEditorValue) return;
      }

      const { content } = toEditorContentRef.current(nextValue);
      isApplyingExternalValueRef.current = true;
      editor.commands.setContent(content);
      isApplyingExternalValueRef.current = false;
      const normalizedValue = toSerializedValueRef.current(editor, resolvedOutputFormat);

      if (normalizedValue !== nextValue) {
        updateHiddenInputValue(normalizedValue, false);
        updateExternalSourceValue(normalizedValue);
      }

      lastValueRef.current = normalizedValue;
      serializedValueRef.current = normalizedValue;
      if (!isControlled) setUncontrolledValue(normalizedValue);
      onExternalValueApplied?.(normalizedValue, editor.state.doc);
    };

    const detachInput = () => {
      if (!attachedInput) return;
      attachedInput.removeEventListener('input', onInput);
      attachedInput.removeEventListener('change', onInput);
      inputObserver?.disconnect();
      inputObserver = null;
      if (attachedInputRef.current === attachedInput) {
        attachedInputRef.current = null;
      }
      attachedInput = null;
    };

    const detachSource = () => {
      if (!attachedSource) return;
      sourceObserver?.disconnect();
      sourceObserver = null;
      if (attachedSourceRef.current === attachedSource) {
        attachedSourceRef.current = null;
      }
      attachedSource = null;
    };

    const onInput = () => {
      if (!attachedInput || !isCurrentInput()) return;
      handleExternalValue(attachedInput.value);
    };

    const applySourceValue = () => {
      if (!attachedSource || !isCurrentSource()) return;
      const sourceValue = attachedSource.getAttribute('data-supertext-value');
      handleExternalValue(typeof sourceValue === 'string' ? sourceValue : '');
    };

    const attachInput = () => {
      if (!hiddenInputId) return true;
      const next = document.getElementById(hiddenInputId);
      if (!isHiddenInputElement(next)) return false;
      if (next === attachedInput) return true;
      if (hasAttachedInput) return false;
      detachInput();
      attachedInput = next;
      attachedInputRef.current = next;
      hasAttachedInput = true;
      attachedInput.addEventListener('input', onInput);
      attachedInput.addEventListener('change', onInput);
      inputObserver = new MutationObserver(mutations => {
        if (!isCurrentInput()) return;
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            handleExternalValue(attachedInput!.value);
          }
        }
      });
      inputObserver.observe(attachedInput, { attributes: true, attributeFilter: ['value'] });
      handleExternalValue(attachedInput.value);
      lastDispatchedInputValueRef.current = attachedInput.value;
      previousDispatchedInputValueRef.current = null;
      lastDispatchedInputAtRef.current = 0;
      return true;
    };

    const attachSource = () => {
      if (!externalValueSourceId) return true;
      const next = document.getElementById(externalValueSourceId);
      if (!(next instanceof HTMLElement)) return false;
      if (next === attachedSource) return true;
      if (hasAttachedSource) return false;
      detachSource();
      attachedSource = next;
      attachedSourceRef.current = next;
      hasAttachedSource = true;
      sourceObserver = new MutationObserver(mutations => {
        if (!isCurrentSource()) return;
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'data-supertext-value') {
            applySourceValue();
          }
        }
      });
      sourceObserver.observe(attachedSource, { attributes: true, attributeFilter: ['data-supertext-value'] });
      applySourceValue();
      return true;
    };

    attachInput();
    attachSource();

    let attempts = 0;
    const maxReattachAttempts = 12;
    const reattachInterval = window.setInterval(() => {
      attempts += 1;
      const hasInput = !hiddenInputId || attachInput();
      const hasSource = !externalValueSourceId || attachSource();
      if ((hasInput && hasSource) || attempts >= maxReattachAttempts) {
        window.clearInterval(reattachInterval);
      }
    }, 300);

    return () => {
      window.clearInterval(reattachInterval);
      detachInput();
      detachSource();
    };
  }, [
    enabled,
    hiddenInputId,
    externalValueSourceId,
    editor,
    isControlled,
    resolvedOutputFormat,
    onExternalValueApplied,
  ]);

  useEffect(() => {
    if (!enabled) return;
    if (!editor || !hiddenInputId) return;
    const handleVisibility = () => {
      if (!editor.isFocused) return;
      triggerHiddenInputChange();
    };
    window.addEventListener('beforeunload', handleVisibility);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('beforeunload', handleVisibility);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [enabled, editor, hiddenInputId, triggerHiddenInputChange]);

  return {
    updateHiddenInputValue,
    getHiddenInputValue,
    triggerHiddenInputChange,
  };
};

type UseControlledEditorSyncParams = {
  editor: Editor | null;
  isControlled: boolean;
  serializedValue: string;
  enableSlashCommands: boolean;
  toEditorContent: ToEditorContent;
  toSerializedValue: ToSerializedValue;
  isApplyingExternalValueRef: MutableRefObject<boolean>;
  paused?: boolean;
};

export const useControlledEditorSync = ({
  editor,
  isControlled,
  serializedValue,
  enableSlashCommands,
  toEditorContent,
  toSerializedValue,
  isApplyingExternalValueRef,
  paused = false,
}: UseControlledEditorSyncParams) => {
  const toEditorContentRef = useRef(toEditorContent);
  const toSerializedValueRef = useRef(toSerializedValue);

  useEffect(() => {
    toEditorContentRef.current = toEditorContent;
  }, [toEditorContent]);

  useEffect(() => {
    toSerializedValueRef.current = toSerializedValue;
  }, [toSerializedValue]);

  useEffect(() => {
    if (paused) return;
    if (!editor || !isControlled) return;

    if (enableSlashCommands) {
      const slashState = slashCommandsPluginKey.getState(editor.state) as { active?: boolean } | null;
      if (slashState?.active || hasPendingSlashTrigger(editor)) {
        return;
      }
    }

    const { content, format } = toEditorContentRef.current(serializedValue);
    const currentValue = toSerializedValueRef.current(editor, format);

    if (currentValue === serializedValue) return;
    if (currentValue === '<p></p>' && serializedValue === '') return;

    isApplyingExternalValueRef.current = true;
    editor.commands.setContent(content);
    isApplyingExternalValueRef.current = false;
  }, [serializedValue, isControlled, editor, enableSlashCommands, paused]);
};
